import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../../../../services/api";
import { useParams, useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Button,
  Breadcrumb,
  Modal,
  Form,
  ListGroup,
  Badge,
  Alert,
  InputGroup,
  Spinner,
  Pagination,
} from "react-bootstrap";
import {
  FaFolder,
  FaFolderOpen,
  FaFile,
  FaVideo,
  FaFilePdf,
  FaPlus,
  FaUpload,
  FaArrowLeft,
  FaTrash,
  FaDownload,
  FaSearch,
  FaThLarge,
  FaList,
  FaHome,
  FaEye,
  FaFileAlt,
  FaFilm,
  FaFileArchive,
  FaYoutube,
  FaPlay,
  FaCopy,
  FaEdit,
} from "react-icons/fa";
import { toast } from "react-toastify";
import Header from "../../../../components/common/Header";
import MaterialService from "../../../../services/materialService";
import CourseService from "../../../../services/courseService";

const MaterialManager = () => {
  const { id: courseId } = useParams();
  const navigate = useNavigate();

  // State for materials and navigation
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [allMaterials, setAllMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("list");

  // Pagination states
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    total_pages: 1,
  });
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modal states
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFileViewModal, setShowFileViewModal] = useState(false);
  const [showYoutubeModal, setShowYoutubeModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedFileForView, setSelectedFileForView] = useState(null);
  const [currentYoutubeVideo, setCurrentYoutubeVideo] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editName, setEditName] = useState("");
  const [editUrl, setEditUrl] = useState("");

  // Form states
  const [newFolderName, setNewFolderName] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadType, setUploadType] = useState("video");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [materialTitle, setMaterialTitle] = useState("");
  const [materialLink, setMaterialLink] = useState("");

  // Adopt Materials Modal states
  const [showAdoptModal, setShowAdoptModal] = useState(false);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [selectedSourceCourse, setSelectedSourceCourse] = useState("");
  const [sourceMaterials, setSourceMaterials] = useState([]);
  const [selectedMaterialIds, setSelectedMaterialIds] = useState([]);
  const [loadingSourceMaterials, setLoadingSourceMaterials] = useState(false);
  const [folderCounts, setFolderCounts] = useState({});

  // Search
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    loadAllMaterials();
  }, []);

  useEffect(() => {
    loadMaterials();
  }, [currentFolder, pagination.current_page, itemsPerPage]);

  useEffect(() => {
    if (currentFolder) {
      loadFolderPath();
    } else {
      setFolderPath([]);
    }
  }, [currentFolder]);

  const loadAllMaterials = async () => {
    try {
      const response = await MaterialService.getMaterials(
        courseId,
        null,
        1,
        1000,
      );
      const allData = response.data?.data || [];
      setAllMaterials(allData);

      // Load counts for all folders
      const folders = allData.filter((item) => item.type === "folder");
      const countsMap = {};
      for (const folder of folders) {
        const count = await getFolderItemCount(folder.id);
        countsMap[folder.id] = count;
      }
      setFolderCounts(countsMap);
    } catch (error) {
      console.error("Failed to load all materials:", error);
    }
  };

  const loadMaterials = async () => {
    try {
      setLoading(true);
      const response = await MaterialService.getMaterials(
        courseId,
        currentFolder,
        pagination.current_page,
        itemsPerPage,
      );

      if (response.data && response.data.data) {
        setMaterials(response.data.data);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }

        // NEW: Load counts for folders in current view
        const currentMaterials = response.data.data;
        const foldersInView = currentMaterials.filter(
          (item) => item.type === "folder",
        );

        if (foldersInView.length > 0) {
          const newCounts = { ...folderCounts };
          for (const folder of foldersInView) {
            if (!newCounts[folder.id] || newCounts[folder.id] === 0) {
              const count = await getFolderItemCount(folder.id);
              newCounts[folder.id] = count;
            }
          }
          setFolderCounts(newCounts);
        }
      } else if (response.data && Array.isArray(response.data)) {
        setMaterials(response.data);
        setPagination({
          current_page: 1,
          per_page: itemsPerPage,
          total: response.data.length,
          total_pages: 1,
        });
      } else {
        setMaterials([]);
      }
    } catch (error) {
      toast.error("Failed to load materials");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadFolderPath = async () => {
    if (!currentFolder) return;
    try {
      const response = await MaterialService.getFolderPath(
        courseId,
        currentFolder,
      );
      setFolderPath(response.data || []);
    } catch (error) {
      console.error("Failed to load folder path:", error);
    }
  };

  const getFolderItemCount = async (folderId) => {
    try {
      const response = await MaterialService.getFolderItemCount(
        courseId,
        folderId,
      );
      return response.data?.count || 0;
    } catch (error) {
      console.error("Failed to get folder count:", error);
      return 0;
    }
  };

  const handleFolderClick = (folder) => {
    setPagination((prev) => ({ ...prev, current_page: 1 }));
    setCurrentFolder(folder.id);
  };

  const handleBreadcrumbClick = (index) => {
    setPagination((prev) => ({ ...prev, current_page: 1 }));
    if (index === -1) {
      setCurrentFolder(null);
    } else {
      setCurrentFolder(folderPath[index].id);
    }
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pagination.total_pages) {
      setPagination((prev) => ({ ...prev, current_page: pageNumber }));
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setItemsPerPage(newLimit);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const renderPagination = () => {
    const { current_page, total_pages } = pagination;

    if (total_pages <= 1) return null;

    let items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, current_page - Math.floor(maxVisible / 2));
    let endPage = Math.min(total_pages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(current_page - 1)}
        disabled={current_page === 1}
      />,
    );

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>,
      );
      if (startPage > 2)
        items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === current_page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>,
      );
    }

    if (endPage < total_pages) {
      if (endPage < total_pages - 1)
        items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
      items.push(
        <Pagination.Item
          key={total_pages}
          onClick={() => handlePageChange(total_pages)}
        >
          {total_pages}
        </Pagination.Item>,
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(current_page + 1)}
        disabled={current_page === total_pages}
      />,
    );

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="d-flex align-items-center gap-2">
          <Form.Select
            size="sm"
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            style={{ width: "auto" }}
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </Form.Select>
          <span className="text-muted small">
            Showing {(pagination.current_page - 1) * itemsPerPage + 1} -{" "}
            {Math.min(pagination.current_page * itemsPerPage, pagination.total)}{" "}
            of {pagination.total} items
          </span>
        </div>
        <Pagination size="sm">{items}</Pagination>
      </div>
    );
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error("Folder name is required");
      return;
    }

    try {
      await MaterialService.createFolder(courseId, {
        name: newFolderName,
        parent_id: currentFolder,
      });
      toast.success("Folder created successfully");
      setShowFolderModal(false);
      setNewFolderName("");
      await loadAllMaterials();
      await loadMaterials();
    } catch (error) {
      toast.error("Failed to create folder");
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setMaterialTitle("");
    setMaterialLink("");
    setUploadProgress(0);
    setUploadType("video");
  };

  const getYoutubeVideoId = (url) => {
    if (!url) return null;
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const handleUploadSubmit = async () => {
    if (uploadType === "link") {
      if (!materialTitle.trim()) {
        toast.error("Title is required");
        return;
      }
      if (!materialLink.trim()) {
        toast.error("URL is required");
        return;
      }
      if (!materialLink.match(/^https?:\/\//)) {
        toast.error(
          "Please enter a valid URL starting with http:// or https://",
        );
        return;
      }

      const formData = new FormData();
      formData.append("parent_id", currentFolder || "");
      formData.append("type", "link");
      formData.append("title", materialTitle);
      formData.append("link_url", materialLink);

      try {
        await MaterialService.uploadFile(courseId, formData);
        toast.success("Link added successfully");
        setShowUploadModal(false);
        resetUploadForm();
        await loadAllMaterials();
        setPagination((prev) => ({ ...prev, current_page: 1 }));
        await loadMaterials();
      } catch (error) {
        toast.error("Failed to add link");
      }
    } else {
      if (!uploadFile) {
        toast.error("Please select a file");
        return;
      }

      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("parent_id", currentFolder || "");
      formData.append("type", uploadType);
      if (materialTitle.trim()) {
        formData.append("title", materialTitle);
      }

      try {
        setUploadProgress(0);
        const interval = setInterval(() => {
          setUploadProgress((prev) => {
            if (prev >= 90) {
              clearInterval(interval);
              return 90;
            }
            return prev + 10;
          });
        }, 200);

        await MaterialService.uploadFile(courseId, formData);
        clearInterval(interval);
        setUploadProgress(100);

        toast.success("File uploaded successfully");
        setShowUploadModal(false);
        resetUploadForm();
        await loadAllMaterials();
        setPagination((prev) => ({ ...prev, current_page: 1 }));
        await loadMaterials();
      } catch (error) {
        toast.error("Failed to upload file");
        setUploadProgress(0);
      }
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;

    try {
      await MaterialService.deleteMaterial(courseId, selectedItem.id);
      toast.success(`${selectedItem.type} deleted successfully`);
      setShowDeleteModal(false);
      setSelectedItem(null);
      await loadAllMaterials();
      await loadMaterials();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const confirmDelete = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const handleViewFile = (file) => {
    setSelectedFileForView(file);
    setShowFileViewModal(true);
  };

  const playYoutubeVideo = (item) => {
    setCurrentYoutubeVideo(item);
    setShowYoutubeModal(true);
  };

  const getYoutubeThumbnail = (url) => {
    const videoId = getYoutubeVideoId(url);
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
    }
    return null;
  };

  const loadCoursesForAdoption = async () => {
    try {
      const response = await CourseService.getCoursesForAdoption(courseId);
      let coursesArray = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          coursesArray = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          coursesArray = response.data.data;
        } else if (
          response.data.courses &&
          Array.isArray(response.data.courses)
        ) {
          coursesArray = response.data.courses;
        } else {
          coursesArray = [];
        }
      } else if (response && Array.isArray(response)) {
        coursesArray = response;
      }
      setAvailableCourses(coursesArray);
    } catch (error) {
      console.error("Failed to load courses:", error);
      toast.error("Failed to load courses");
      setAvailableCourses([]);
    }
  };

  const loadSourceCourseMaterials = async (sourceCourseId) => {
    if (!sourceCourseId) {
      setSourceMaterials([]);
      return;
    }

    setLoadingSourceMaterials(true);
    try {
      const response =
        await MaterialService.getAllMaterialsForCourse(sourceCourseId);
      setSourceMaterials(response.data?.tree || []);
    } catch (error) {
      toast.error("Failed to load source course materials");
      console.error(error);
    } finally {
      setLoadingSourceMaterials(false);
    }
  };

  const handleSourceCourseChange = async (e) => {
    const sourceId = e.target.value;
    setSelectedSourceCourse(sourceId);
    setSelectedMaterialIds([]);
    if (sourceId) {
      await loadSourceCourseMaterials(sourceId);
    } else {
      setSourceMaterials([]);
    }
  };

  const toggleMaterialSelection = (materialId, isChecked) => {
    if (isChecked) {
      setSelectedMaterialIds((prev) => [...prev, materialId]);
    } else {
      setSelectedMaterialIds((prev) => prev.filter((id) => id !== materialId));
    }
  };

  const toggleSelectAll = () => {
    const allIds = getAllMaterialIdsFromTree(sourceMaterials);
    if (selectedMaterialIds.length === allIds.length && allIds.length > 0) {
      setSelectedMaterialIds([]);
    } else {
      setSelectedMaterialIds(allIds);
    }
  };

  const getAllMaterialIdsFromTree = (items) => {
    let ids = [];
    items.forEach((item) => {
      ids.push(item.id);
      if (item.children && item.children.length > 0) {
        ids = [...ids, ...getAllMaterialIdsFromTree(item.children)];
      }
    });
    return ids;
  };

  // Get all child IDs inside a folder (recursive, including nested folders)
  const getAllChildIds = (item) => {
    let ids = [];
    if (item.children && item.children.length > 0) {
      item.children.forEach((child) => {
        ids.push(child.id);
        if (child.children && child.children.length > 0) {
          ids = [...ids, ...getAllChildIds(child)];
        }
      });
    }
    return ids;
  };

  // Toggle select/deselect all children inside a folder
  const toggleFolderChildren = (item) => {
    const childIds = getAllChildIds(item);
    const allSelected = childIds.every((id) => selectedMaterialIds.includes(id));
    if (allSelected) {
      // Deselect all children
      setSelectedMaterialIds((prev) => prev.filter((id) => !childIds.includes(id)));
    } else {
      // Select all children
      setSelectedMaterialIds((prev) => {
        const newIds = [...prev];
        childIds.forEach((id) => {
          if (!newIds.includes(id)) newIds.push(id);
        });
        return newIds;
      });
    }
  };

  const handleAdoptMaterials = async () => {
    if (!selectedSourceCourse) {
      toast.error("Please select a source course");
      return;
    }

    if (selectedMaterialIds.length === 0) {
      toast.error("Please select at least one material to import");
      return;
    }

    try {
      const response = await MaterialService.adoptMaterials(
        selectedSourceCourse,
        courseId,
        selectedMaterialIds,
        currentFolder,
      );

      toast.success(
        response.message ||
          `${response.data?.copied_count || selectedMaterialIds.length} materials imported successfully`,
      );

      setShowAdoptModal(false);
      setSelectedSourceCourse("");
      setSourceMaterials([]);
      setSelectedMaterialIds([]);

      await loadAllMaterials();
      setPagination((prev) => ({ ...prev, current_page: 1 }));
      await loadMaterials();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to import materials");
      console.error(error);
    }
  };

  const renderAdoptMaterialTree = (items, level = 0) => {
    return items.map((item) => {
      const isChecked = selectedMaterialIds.includes(item.id);
      const isFolder = item.type === "folder";
      const childIds = isFolder ? getAllChildIds(item) : [];
      const allChildrenSelected = isFolder && childIds.length > 0 && childIds.every((id) => selectedMaterialIds.includes(id));

      return (
        <div
          key={item.id}
          style={{ marginLeft: `${level * 25}px` }}
          className="mb-2"
        >
          <div className="d-flex align-items-center">
            <Form.Check
              type="checkbox"
              id={`adopt-checkbox-${item.id}`}
              checked={isChecked}
              onChange={(e) =>
                toggleMaterialSelection(item.id, e.target.checked)
              }
              className="me-2"
            />
            {isFolder ? (
              <FaFolder className="text-warning me-2" />
            ) : item.type === "video" ? (
              <FaVideo className="text-primary me-2" />
            ) : item.type === "pdf" ? (
              <FaFilePdf className="text-danger me-2" />
            ) : item.type === "link" ? (
              <FaYoutube className="text-danger me-2" />
            ) : item.type === "zip" ? (
              <FaFileArchive className="text-success me-2" />
            ) : (
              <FaFileAlt className="text-secondary me-2" />
            )}
            <span>{item.name}</span>
            <Badge bg="secondary" className="ms-2">
              {item.type}
            </Badge>
            {isFolder && childIds.length > 0 && (
              <Button
                variant={allChildrenSelected ? "outline-danger" : "outline-primary"}
                size="sm"
                className="ms-2 py-0 px-2"
                style={{ fontSize: '0.7rem' }}
                onClick={() => toggleFolderChildren(item)}
              >
                {allChildrenSelected ? "Deselect All" : "Select All"}
              </Button>
            )}
          </div>
          {item.children &&
            item.children.length > 0 &&
            renderAdoptMaterialTree(item.children, level + 1)}
        </div>
      );
    });
  };

  const getIcon = (type, size = 20) => {
    switch (type) {
      case "folder":
        return <FaFolder className="text-warning" size={size} />;
      case "video":
        return <FaVideo className="text-primary" size={size} />;
      case "pdf":
        return <FaFilePdf className="text-danger" size={size} />;
      case "note":
        return <FaFileAlt className="text-info" size={size} />;
      case "link":
        return <FaYoutube className="text-danger" size={size} />;
      case "zip":
        return <FaFileArchive className="text-success" size={size} />;
      default:
        return <FaFile className="text-secondary" size={size} />;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  const getFileUrl = (filePath) => {
    return `${API_BASE_URL}/${filePath}`;
  };

  const getFileTypeIcon = (type) => {
    switch (type) {
      case "video":
        return <FaFilm size={30} className="text-primary" />;
      case "pdf":
        return <FaFilePdf size={30} className="text-danger" />;
      case "note":
        return <FaFileAlt size={30} className="text-info" />;
      case "link":
        return <FaYoutube size={30} className="text-danger" />;
      case "zip":
        return <FaFileArchive size={30} className="text-success" />;
      default:
        return <FaFileArchive size={30} className="text-secondary" />;
    }
  };

  const filteredMaterials = materials.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleEditClick = (item, e) => {
    if (e) e.stopPropagation();
    setEditingItem(item);
    setEditName(item.name);
    setEditUrl(item.url_link || "");
    setShowEditModal(true);
  };

  const handleUpdateMaterial = async () => {
    if (!editName.trim()) {
      toast.error("Name is required");
      return;
    }

    const updateData = { name: editName };
    if (editingItem.type === "link" && editUrl) {
      if (!editUrl.match(/^https?:\/\//)) {
        toast.error("Please enter a valid URL");
        return;
      }
      updateData.url_link = editUrl;
    }

    try {
      await MaterialService.updateMaterial(
        courseId,
        editingItem.id,
        updateData,
      );
      toast.success("Updated successfully");
      setShowEditModal(false);
      setEditingItem(null);
      await loadAllMaterials();
      await loadMaterials();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  return (
    <div>
      <Header />
      <div className="container-fluid p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <Button
            variant="link"
            className="text-decoration-none p-0"
            onClick={() => navigate(`/admin/courses/${courseId}`)}
          >
            <FaArrowLeft className="me-2" /> Back to Course Details
          </Button>

          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              className="me-2"
              onClick={() => setViewMode(viewMode === "list" ? "grid" : "list")}
            >
              {viewMode === "list" ? <FaThLarge /> : <FaList />}
            </Button>
          </div>
        </div>

        <Card className="shadow">
          <Card.Header className="bg-primary text-white py-3">
            <Row className="align-items-center">
              <Col md={4}>
                <h4 className="mb-0">
                  <FaFolderOpen className="me-2" />
                  Course Materials
                </h4>
              </Col>
              <Col md={4}>
                <InputGroup>
                  <InputGroup.Text className="bg-white">
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search files and folders..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={4} className="text-end">
                <Button
                  variant="light"
                  size="sm"
                  className="me-2"
                  onClick={() => setShowFolderModal(true)}
                >
                  <FaPlus className="me-1" /> New Folder
                </Button>
                <Button
                  variant="info"
                  size="sm"
                  className="me-2"
                  onClick={() => {
                    loadCoursesForAdoption();
                    setShowAdoptModal(true);
                  }}
                >
                  <FaCopy className="me-1" /> Import Materials
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => {
                    resetUploadForm();
                    setShowUploadModal(true);
                  }}
                >
                  <FaUpload className="me-1" /> Upload
                </Button>
              </Col>
            </Row>
          </Card.Header>

          <Card.Body>
            <Breadcrumb className="mb-4 p-3 bg-light rounded">
              <Breadcrumb.Item
                onClick={() => handleBreadcrumbClick(-1)}
                active={!currentFolder}
                className={!currentFolder ? "fw-bold" : ""}
              >
                <FaHome className="me-1" />
                Root
              </Breadcrumb.Item>
              {folderPath.map((folder, index) => (
                <Breadcrumb.Item
                  key={folder.id}
                  onClick={() => handleBreadcrumbClick(index)}
                  active={index === folderPath.length - 1}
                  className={index === folderPath.length - 1 ? "fw-bold" : ""}
                >
                  {folder.name}
                </Breadcrumb.Item>
              ))}
            </Breadcrumb>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <>
                {filteredMaterials.length === 0 ? (
                  <Alert variant="info" className="text-center py-5">
                    <FaFolder className="mb-3" size={50} />
                    <h5>This folder is empty</h5>
                    <p className="mb-0">
                      Create a new folder or upload files to get started.
                    </p>
                  </Alert>
                ) : viewMode === "list" ? (
                  <>
                    <ListGroup>
                      {filteredMaterials.map((item) => (
                        <ListGroup.Item
                          key={item.id}
                          className="d-flex justify-content-between align-items-center hover-shadow py-3"
                          style={{
                            cursor:
                              item.type === "folder" ? "pointer" : "default",
                          }}
                          onClick={() =>
                            item.type === "folder" && handleFolderClick(item)
                          }
                        >
                          <div className="d-flex align-items-center">
                            {getIcon(item.type, 20)}
                            <div className="ms-3">
                              <strong>{item.name}</strong>
                              {item.type === "folder" ? (
                                <Badge bg="secondary" className="ms-2">
                                  {folderCounts[item.id] || 0}{" "}
                                  {folderCounts[item.id] === 1
                                    ? "item"
                                    : "items"}
                                </Badge>
                              ) : item.type === "link" ? (
                                <Badge bg="danger" className="ms-2">
                                  YouTube Video
                                </Badge>
                              ) : item.type === "zip" ? (
                                <Badge bg="success" className="ms-2">
                                  📦 ZIP Archive
                                </Badge>
                              ) : (
                                <div>
                                  <small className="text-muted">
                                    {formatFileSize(item.file_size)} •{" "}
                                    {item.mime_type?.split("/")[1] || item.type}
                                  </small>
                                </div>
                              )}
                            </div>
                          </div>

                          <div>
                            {item.type !== "folder" && (
                              <>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="text-secondary me-1"
                                  title="Move Up"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      await MaterialService.moveUp(
                                        courseId,
                                        item.id,
                                      );
                                      toast.success("Material moved up");
                                      await loadMaterials();
                                    } catch (error) {
                                      toast.error("Failed to move material");
                                    }
                                  }}
                                  disabled={materials.indexOf(item) === 0}
                                >
                                  ↑
                                </Button>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="text-secondary me-1"
                                  title="Move Down"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      await MaterialService.moveDown(
                                        courseId,
                                        item.id,
                                      );
                                      toast.success("Material moved down");
                                      await loadMaterials();
                                    } catch (error) {
                                      toast.error("Failed to move material");
                                    }
                                  }}
                                  disabled={
                                    materials.indexOf(item) ===
                                    materials.length - 1
                                  }
                                >
                                  ↓
                                </Button>

                                {item.type === "link" ? (
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="text-danger me-2"
                                    title="Play Video"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      playYoutubeVideo(item);
                                    }}
                                  >
                                    <FaPlay /> Play
                                  </Button>
                                ) : item.type === "video" ? (
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="text-info me-2"
                                    title="Play Video"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewFile(item);
                                    }}
                                  >
                                    <FaPlay /> Play
                                  </Button>
                                ) : (
                                  <>
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="text-info me-2"
                                      title="View"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewFile(item);
                                      }}
                                    >
                                      <FaEye />
                                    </Button>
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="text-primary me-2"
                                      title="Download"
                                      href={getFileUrl(item.file_path)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <FaDownload />
                                    </Button>
                                  </>
                                )}
                              </>
                            )}
                            <Button
                              variant="link"
                              size="sm"
                              className="text-warning me-2"
                              title="Edit"
                              onClick={(e) => handleEditClick(item, e)}
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              className="text-danger"
                              title="Delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                confirmDelete(item);
                              }}
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                    {renderPagination()}
                  </>
                ) : (
                  <Row xs={2} md={4} lg={6} className="g-4">
                    {filteredMaterials.map((item) => {
                      const thumbnail =
                        item.type === "link"
                          ? getYoutubeThumbnail(item.url_link)
                          : null;
                      return (
                        <Col key={item.id}>
                          <Card
                            className="h-100 text-center hover-shadow"
                            style={{
                              cursor:
                                item.type === "folder" ? "pointer" : "default",
                            }}
                            onClick={() =>
                              item.type === "folder" && handleFolderClick(item)
                            }
                          >
                            <Card.Body className="d-flex flex-column align-items-center">
                              {item.type === "link" && thumbnail ? (
                                <img
                                  src={thumbnail}
                                  alt={item.name}
                                  style={{
                                    width: "100%",
                                    height: "100px",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              ) : (
                                getIcon(item.type, 40)
                              )}
                              <Card.Title
                                className="fs-6 mt-2 text-truncate w-100"
                                title={item.name}
                              >
                                {item.name}
                              </Card.Title>
                              {item.type === "folder" ? (
                                <Badge bg="secondary" pill>
                                  {folderCounts[item.id] || 0}{" "}
                                  {folderCounts[item.id] === 1
                                    ? "item"
                                    : "items"}
                                </Badge>
                              ) : item.type === "link" ? (
                                <Badge bg="danger" pill>
                                  🎬 YouTube
                                </Badge>
                              ) : item.type === "video" ? (
                                <Badge bg="primary" pill>
                                  🎥 Video
                                </Badge>
                              ) : item.type === "zip" ? (
                                <Badge bg="success" pill>
                                  📦 ZIP
                                </Badge>
                              ) : (
                                <small className="text-muted">
                                  {formatFileSize(item.file_size)}
                                </small>
                              )}
                            </Card.Body>
                            <Card.Footer className="bg-transparent border-0">
                              {item.type !== "folder" && (
                                <>
                                  {item.type === "link" ? (
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      className="me-1"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        playYoutubeVideo(item);
                                      }}
                                    >
                                      <FaPlay /> Play
                                    </Button>
                                  ) : item.type === "video" ? (
                                    <Button
                                      variant="outline-info"
                                      size="sm"
                                      className="me-1"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewFile(item);
                                      }}
                                    >
                                      <FaPlay /> Play
                                    </Button>
                                  ) : (
                                    <>
                                      <Button
                                        variant="outline-info"
                                        size="sm"
                                        className="me-1"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleViewFile(item);
                                        }}
                                      >
                                        <FaEye />
                                      </Button>
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="me-1"
                                        href={getFileUrl(item.file_path)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <FaDownload />
                                      </Button>
                                    </>
                                  )}
                                </>
                              )}
                              <Button
                                variant="outline-warning"
                                size="sm"
                                className="me-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditClick(item, e);
                                }}
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  confirmDelete(item);
                                }}
                              >
                                <FaTrash />
                              </Button>
                            </Card.Footer>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </>
            )}
            {viewMode === "grid" && renderPagination()}
          </Card.Body>
        </Card>

        {/* Create Folder Modal */}
        <Modal show={showFolderModal} onHide={() => setShowFolderModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FaFolder className="me-2 text-warning" />
              Create New Folder
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group>
                <Form.Label>Folder Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter folder name"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  autoFocus
                />
              </Form.Group>
              {currentFolder && (
                <Alert variant="info" className="mt-3">
                  Creating folder inside:{" "}
                  <strong>
                    {folderPath[folderPath.length - 1]?.name || "Root"}
                  </strong>
                </Alert>
              )}
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowFolderModal(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateFolder}>
              Create Folder
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Upload/Link Modal */}
        <Modal
          show={showUploadModal}
          onHide={() => {
            setShowUploadModal(false);
            resetUploadForm();
          }}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <FaUpload className="me-2 text-success" />
              Add Materials
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Material Type</Form.Label>
                <div>
                  <Form.Check
                    inline
                    type="radio"
                    label="Video (Upload)"
                    name="uploadType"
                    value="video"
                    checked={uploadType === "video"}
                    onChange={(e) => setUploadType(e.target.value)}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    label="PDF"
                    name="uploadType"
                    value="pdf"
                    checked={uploadType === "pdf"}
                    onChange={(e) => setUploadType(e.target.value)}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    label="Notes"
                    name="uploadType"
                    value="note"
                    checked={uploadType === "note"}
                    onChange={(e) => setUploadType(e.target.value)}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    label="ZIP File"
                    name="uploadType"
                    value="zip"
                    checked={uploadType === "zip"}
                    onChange={(e) => setUploadType(e.target.value)}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    label="YouTube Link"
                    name="uploadType"
                    value="link"
                    checked={uploadType === "link"}
                    onChange={(e) => setUploadType(e.target.value)}
                  />
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Title *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter title for this material"
                  value={materialTitle}
                  onChange={(e) => setMaterialTitle(e.target.value)}
                />
                <Form.Text className="text-muted">
                  Display name for students
                </Form.Text>
              </Form.Group>

              {uploadType === "link" ? (
                <Form.Group className="mb-3">
                  <Form.Label>YouTube/Video URL *</Form.Label>
                  <Form.Control
                    type="url"
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={materialLink}
                    onChange={(e) => setMaterialLink(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Enter the full YouTube URL (must be unlisted or public for
                    embedding)
                  </Form.Text>
                </Form.Group>
              ) : (
                <Form.Group className="mb-3">
                  <Form.Label>Select File</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    accept={
                      uploadType === "video"
                        ? "video/*"
                        : uploadType === "pdf"
                        ? ".pdf"
                        : uploadType === "zip"
                        ? ".zip,.rar,.7z"
                        : ".doc,.docx,.ppt,.pptx,.txt"
                    }
                  />
                  <Form.Text className="text-muted">
                    {uploadType === "video" &&
                      "Supported: MP4, AVI, MKV, MOV, etc."}
                    {uploadType === "pdf" && "Supported: PDF files only"}
                    {uploadType === "zip" && "Supported: ZIP, RAR, 7Z archive files"}
                    {uploadType === "note" &&
                      "Supported: DOC, DOCX, PPT, PPTX, TXT"}
                  </Form.Text>
                </Form.Group>
              )}

              {uploadProgress > 0 && uploadType !== "link" && (
                <div className="mb-3">
                  <Form.Label>Upload Progress</Form.Label>
                  <div className="progress">
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated"
                      role="progressbar"
                      style={{ width: `${uploadProgress}%` }}
                      aria-valuenow={uploadProgress}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    >
                      {uploadProgress}%
                    </div>
                  </div>
                </div>
              )}

              {currentFolder && (
                <Alert variant="info">
                  Adding to:{" "}
                  <strong>
                    {folderPath[folderPath.length - 1]?.name || "Root"}
                  </strong>{" "}
                  folder
                </Alert>
              )}
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowUploadModal(false);
                resetUploadForm();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUploadSubmit}
              disabled={
                (uploadType !== "link" && !uploadFile) ||
                (uploadType === "link" && (!materialTitle || !materialLink))
              }
            >
              {uploadProgress > 0
                ? "Uploading..."
                : uploadType === "link"
                  ? "Add Link"
                  : "Upload File"}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Import Materials Modal */}
        <Modal
          show={showAdoptModal}
          onHide={() => {
            setShowAdoptModal(false);
            setSelectedSourceCourse("");
            setSourceMaterials([]);
            setSelectedMaterialIds([]);
          }}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <FaCopy className="me-2 text-info" />
              Import Materials from Another Course
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="info">
              <strong>How it works:</strong> Select a source course, choose the
              materials you want to copy, and they will be imported into {currentFolder ? 'the current folder' : 'the root'} of this course. File references are copied (not duplicated), saving
              storage space.
            </Alert>

            <Form.Group className="mb-4">
              <Form.Label>Select Source Course</Form.Label>
              <Form.Select
                value={selectedSourceCourse}
                onChange={handleSourceCourseChange}
              >
                <option value="">-- Select a course --</option>
                {Array.isArray(availableCourses) &&
                availableCourses.length > 0 ? (
                  availableCourses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.name}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    No other courses available
                  </option>
                )}
              </Form.Select>
              <Form.Text className="text-muted">
                Materials will be copied from this course to the current course
              </Form.Text>
            </Form.Group>

            {selectedSourceCourse && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Materials to Import:</h6>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={toggleSelectAll}
                  >
                    {selectedMaterialIds.length ===
                      getAllMaterialIdsFromTree(sourceMaterials).length &&
                    getAllMaterialIdsFromTree(sourceMaterials).length > 0
                      ? "Deselect All"
                      : "Select All"}
                  </Button>
                </div>

                {loadingSourceMaterials ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" size="sm" />
                    <p className="mt-2">Loading materials...</p>
                  </div>
                ) : sourceMaterials.length === 0 ? (
                  <Alert variant="warning">
                    No materials found in the selected course.
                  </Alert>
                ) : (
                  <div
                    className="border rounded p-3"
                    style={{ maxHeight: "400px", overflowY: "auto" }}
                  >
                    {renderAdoptMaterialTree(sourceMaterials)}
                  </div>
                )}

                <div className="mt-3">
                  <Badge bg="primary">
                    Selected: {selectedMaterialIds.length} material(s)
                  </Badge>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowAdoptModal(false);
                setSelectedSourceCourse("");
                setSourceMaterials([]);
                setSelectedMaterialIds([]);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAdoptMaterials}
              disabled={
                !selectedSourceCourse || selectedMaterialIds.length === 0
              }
            >
              <FaCopy className="me-2" /> Import Selected Materials
            </Button>
          </Modal.Footer>
        </Modal>

        {/* File View Modal */}
        <Modal
          show={showFileViewModal}
          onHide={() => setShowFileViewModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title className="d-flex align-items-center">
              {selectedFileForView && getFileTypeIcon(selectedFileForView.type)}
              <span className="ms-2">{selectedFileForView?.name}</span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
            {selectedFileForView && (
              <>
                {selectedFileForView.type === "video" ? (
                  <div className="bg-dark">
                    <video
                      controls
                      autoPlay
                      className="w-100"
                      style={{ maxHeight: "70vh" }}
                      src={getFileUrl(selectedFileForView.file_path)}
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                ) : selectedFileForView.type === "pdf" ? (
                  <div style={{ height: "70vh" }}>
                    <iframe
                      src={getFileUrl(selectedFileForView.file_path)}
                      className="w-100 h-100"
                      title={selectedFileForView.name}
                    />
                  </div>
                ) : (
                  <div className="text-center py-5">
                    {getFileTypeIcon(selectedFileForView.type)}
                    <h5 className="mt-3">File Preview Not Available</h5>
                    <p className="text-muted">
                      This file type cannot be previewed. Please download to
                      view.
                    </p>
                    <Button
                      variant="primary"
                      href={getFileUrl(selectedFileForView.file_path)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaDownload className="me-2" /> Download File
                    </Button>
                  </div>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <div className="d-flex justify-content-between w-100 align-items-center">
              <div className="text-muted small">
                {selectedFileForView && selectedFileForView.type !== "link" && (
                  <>
                    Size: {formatFileSize(selectedFileForView.file_size)} •
                    Type:{" "}
                    {selectedFileForView.mime_type || selectedFileForView.type}
                  </>
                )}
              </div>
              <Button
                variant="secondary"
                onClick={() => setShowFileViewModal(false)}
              >
                Close
              </Button>
            </div>
          </Modal.Footer>
        </Modal>

        {/* YouTube Player Modal */}
        <Modal
          show={showYoutubeModal}
          onHide={() => setShowYoutubeModal(false)}
          size="lg"
          centered
          className="youtube-player-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title className="d-flex align-items-center">
              <FaYoutube className="text-danger me-2" />
              {currentYoutubeVideo?.name}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
            {currentYoutubeVideo && (
              <div
                style={{
                  position: "relative",
                  paddingBottom: "56.25%",
                  height: 0,
                }}
              >
                <iframe
                  src={`https://www.youtube.com/embed/${getYoutubeVideoId(currentYoutubeVideo.url_link)}?autoplay=1&rel=0`}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={currentYoutubeVideo.name}
                />
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowYoutubeModal(false)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title className="text-danger">
              <FaTrash className="me-2" />
              Confirm Delete
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedItem && (
              <>
                <p>Are you sure you want to delete:</p>
                <p className="fw-bold text-danger">{selectedItem.name}</p>
                {selectedItem.type === "folder" ? (
                  <Alert variant="warning">
                    <strong>Warning:</strong> This folder and all its contents (
                    {folderCounts[selectedItem.id] || 0} items) will be
                    permanently deleted.
                  </Alert>
                ) : (
                  <p className="text-muted">This action cannot be undone.</p>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Delete Permanently
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Edit Modal */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FaEdit className="me-2 text-warning" />
              Edit {editingItem?.type === "folder" ? "Folder" : "Material"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Enter name"
                />
              </Form.Group>
              {editingItem?.type === "link" && (
                <Form.Group className="mb-3">
                  <Form.Label>URL</Form.Label>
                  <Form.Control
                    type="url"
                    value={editUrl}
                    onChange={(e) => setEditUrl(e.target.value)}
                    placeholder="https://..."
                  />
                </Form.Group>
              )}
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleUpdateMaterial}>
              Save Changes
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default MaterialManager;